import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Typography,
  message,
  // Alert,
  Spin,
} from "antd";
import { ClockCircleOutlined, CheckOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import "react-quill/dist/quill.snow.css";
import { useNavigate } from "react-router-dom";
import { db, storage as firebaseStorage } from "../../../firebase/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  serverTimestamp,
  DocumentReference,
  Timestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage as localStorageUtil } from "utils/storage";
import RegisterRoleSvg from "../../../assets/register.svg";
import EducationalBackgroundForm from "./components/updateEducationalBackground";
import EexperienceForm from "./components/updateExperience";
import CertificatestForm from "./components/updateCertificates";
import PermanentAddressForm from "./components/updatePermanentAddress";
import TemporaryAddressForm from "./components/updateTemporaryAddress";
import BasicInformationForm from "./components/updateBasicInformation";
import CvUpload from "./components/updateCvUpload";
import {
  AddressInterface,
  CertificateInterface,
  EnumeratorInterface,
  LanguageInterface,
  EducationalBackgroundInterface,
  ExperienceInterface,
  CVInterface,
} from "api/enumerator/Profile/dto";

const { Title } = Typography;

const fetchUserInfo = async (
  uid: string
): Promise<EnumeratorInterface & { docRef: DocumentReference }> => {
  try {
    const userInfoQuery = query(
      collection(db, "user_infos"),
      where("uid", "==", uid)
    );
    const querySnapshot = await getDocs(userInfoQuery);
    if (querySnapshot.empty) throw new Error("User profile not found.");
    const userDoc = querySnapshot.docs[0];

    return { ...userDoc.data(), docRef: userDoc.ref } as EnumeratorInterface & {
      docRef: DocumentReference;
    };
  } catch (error: any) {
    throw new Error("Error fetching user profile: " + error.message);
  }
};

const updateUserProfile = async (
  userData: any,
  firstname: string,
  lastname: string,
  middlename: string,
  gender: string,
  email: string,
  age: string,
  dateOfBirth: string,
  permanentAddress: AddressInterface,
  temporaryAddress: AddressInterface,
  phoneNumber: string,
  language: LanguageInterface[],
  educationalBackground: EducationalBackgroundInterface[],
  experience: ExperienceInterface[],
  cv: CVInterface
) => {
  try {
    if (!userData?.docRef) {
      console.warn("User profile does not exist. No update performed.");
      return;
    }
    const updatedProfile = {
      role: userData.role || "",
      firstName: firstname || "",
      lastName: lastname || "",
      middleName: middlename || "",
      gender: gender || "",
      email: email || "",
      age: age || "",
      dateOfBirth: dateOfBirth || "",
      phoneNumber: phoneNumber || "",
      permanentAddress: permanentAddress,
      temporaryAddress: temporaryAddress,
      languages: language,
      educationalBackground: educationalBackground,
      experience: experience,
      cv: cv,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(userData.docRef, updatedProfile);
  } catch (error: any) {
    throw new Error("Error updating user profile: " + error.message);
  }
};

interface UpdatedCertificate {
  title: string;
  issuedDate: string | null;
  expiresOn: string | null;
  fileUrl: string;
  fileName: string;
}

interface ProfileFormValues {
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  phoneNumber: string;
  age: string;
  gender: string;
  dateOfBirth: Dayjs | null;
  permanentAddress: AddressInterface;
  temporaryAddress: AddressInterface;
  languages: LanguageInterface[];
  educationalBackground: EducationalBackgroundInterface[];
  experience: ExperienceInterface[];
  certificates: CertificateInterface[];
  cv: CVInterface;
}

const EnumeratorUpdateProfileForm: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);

  const storedUser = localStorageUtil.getUserFromStorage();
  const [form] = Form.useForm<ProfileFormValues>();
  const navigate = useNavigate();

  const [userinfo, setUserInfo] = useState<EnumeratorInterface>();

  useEffect(() => {
    const fetchAndSetUserData = async () => {
      try {
        const userInfo = await fetchUserInfo(storedUser.uid);
        setUserInfo(userInfo);

        const dateOfBirth = userInfo.dateOfBirth
          ? dayjs(userInfo.dateOfBirth)
          : null;

        const formattedEducationBackground =
          userInfo.educationalBackground?.map((education: any) => ({
            ...education,
            startDate: education.startDate
              ? dayjs(education.startDate.toDate())
              : null,
            endDate: education.endDate
              ? dayjs(education.endDate.toDate())
              : null,
          })) || [{}]; // Default to [{}] if empty

        const formattedExperience = (userInfo.experience || []).map(
          (experience: any) => ({
            ...experience,
            startDate: experience.startDate
              ? dayjs(experience.startDate.toDate())
              : null,
            endDate: experience.endDate
              ? dayjs(experience.endDate.toDate())
              : null,
          })
        );

        // Only set fields after fetching the data
        form.setFieldsValue({
          firstName: userInfo.firstName || "",
          lastName: userInfo.lastName || "",
          middleName: userInfo.middleName || "",
          email: userInfo.email || "",
          phoneNumber: userInfo.phoneNumber || "",
          age: userInfo.age || "",
          gender: userInfo.gender || "",
          dateOfBirth: dateOfBirth,

          permanentAddress: userInfo.permanentAddress || {
            street: "",
            city: "",
            province: "",
            zip: "",
          },
          temporaryAddress: userInfo.temporaryAddress || {
            street: "",
            city: "",
            province: "",
            zip: "",
          },
          languages: userInfo.languages ? userInfo.languages : [],
          educationalBackground:
            formattedEducationBackground.length > 0
              ? formattedEducationBackground
              : [{}],
          experience:
            formattedExperience.length > 0 ? formattedExperience : [{}], // Ensure at least [{}]

          certificates: (userInfo.certificates || []).map((cert: any) => ({
            title: cert.title,
            issuedDate: cert.issuedDate ? dayjs(cert.issuedDate) : null,
            expiresOn: cert.expiresOn ? dayjs(cert.expiresOn) : null,
            files: [
              {
                uid: cert.fileUrl,
                name: cert.fileName,
                status: "done",
                url: cert.fileUrl,
              },
            ],
          })),
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        message.error("Failed to load user profile.");
        setLoading(false);
      }
    };

    fetchAndSetUserData();
  }, [form, storedUser.uid]);

  const deleteCertificateFile = async (fileUrl: string) => {
    try {
      const fileRef = ref(firebaseStorage, fileUrl);
      await deleteObject(fileRef);
    } catch (error) {
      console.error("Error deleting certificate file:", error);
    }
  };

  const handleSubmit = async (values: ProfileFormValues) => {
    form.getFieldValue();
    try {
      setLoading(true);
      if (!storedUser?.uid) {
        message.error("User not authenticated.");
        return;
      }

      const userInfo = await fetchUserInfo(storedUser.uid);
      if (!userInfo) {
        message.error("User profile not found.");
        return;
      }

      // Process educational background
      const formattedEducationBackground = values.educationalBackground.map(
        (education: any) => ({
          ...education,
          startDate: education.startDate
            ? Timestamp.fromDate(education.startDate.toDate())
            : null,
          endDate: education.endDate
            ? Timestamp.fromDate(education.endDate.toDate())
            : null,
        })
      );

      // Process experience
      const formattedExperience = values.experience.map((experience: any) => ({
        ...experience,
        currentlyWorking: experience.currentlyWorking ? true : false,
        descriptions: experience.descriptions || "",
        startDate: experience.startDate
          ? Timestamp.fromDate(experience.startDate.toDate())
          : null,
        endDate: experience.endDate
          ? Timestamp.fromDate(experience.endDate.toDate())
          : null,
      }));

      // Process certificates
      let processedCertificates: UpdatedCertificate[] = [];
      if (values.certificates && values.certificates.length > 0) {
        const certsFolderRef = ref(
          firebaseStorage,
          `enumerators/${userInfo.uid}/certificates`
        );
        processedCertificates = (
          await Promise.all(
            values.certificates.map(
              async (
                cert: CertificateInterface
              ): Promise<UpdatedCertificate | null> => {
                const file = cert.files && cert.files[0];
                if (!file) return null;

                if (file.status === "done") {
                  return {
                    title: cert.title,
                    issuedDate: cert.issuedDate
                      ? dayjs(cert.issuedDate).format("YYYY-MM-DD")
                      : null,
                    expiresOn: cert.expiresOn
                      ? dayjs(cert.expiresOn).format("YYYY-MM-DD")
                      : null,
                    fileUrl: file.url,
                    fileName: file.name,
                  };
                } else {
                  const certRef = ref(certsFolderRef, file.name);
                  await uploadBytes(certRef, file);
                  const downloadURL = await getDownloadURL(certRef);
                  return {
                    title: cert.title,
                    issuedDate: cert.issuedDate
                      ? dayjs(cert.issuedDate).format("YYYY-MM-DD")
                      : null,
                    expiresOn: cert.expiresOn
                      ? dayjs(cert.expiresOn).format("YYYY-MM-DD")
                      : null,
                    fileUrl: downloadURL,
                    fileName: file.name,
                  };
                }
              }
            )
          )
        ).filter((cert): cert is UpdatedCertificate => cert !== null);
      }

      const previousCertificates: UpdatedCertificate[] = (
        userInfo.certificates || []
      ).map((cert: any) => ({
        title: cert.title,
        issuedDate: cert.issuedDate,
        expiresOn: cert.expiresOn,
        fileUrl: cert.fileUrl,
        fileName: cert.fileName,
      }));
      const newFileUrls = processedCertificates.map((cert) => cert.fileUrl);
      const certificatesToDelete = previousCertificates.filter(
        (cert) => !newFileUrls.includes(cert.fileUrl)
      );
      await Promise.all(
        certificatesToDelete.map((cert) => deleteCertificateFile(cert.fileUrl))
      );

      console.log(values);

      await updateUserProfile(
        userInfo,
        values.firstName,
        values.lastName,
        values.middleName,
        values.gender,
        values.email,
        values.age,
        values.dateOfBirth
          ? dayjs(values.dateOfBirth).format("YYYY-MM-DD")
          : "",
        values.permanentAddress,
        values.temporaryAddress,
        values.phoneNumber,
        values.languages,
        formattedEducationBackground,
        formattedExperience,
        values.cv
      );

      await updateDoc(userInfo.docRef, {
        certificates: processedCertificates,
        updatedAt: serverTimestamp(),
      });

      message.success("Profile saved!");
      navigate("/enumerator/profile");
    } catch (error: any) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <div className="bg-background min-h-screen px-5 md:px-0">
        <div className="max-w-6xl mx-auto py-12">
          <Card className="px-8 py-2 mt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <Title level={2} className="font-bold text-3xl">
                Let's setup your profile
              </Title>
              <img src={RegisterRoleSvg} alt="Register Role Illustration" />
            </div>
          </Card>
          <Form layout="vertical" form={form} onFinish={handleSubmit}>
            <Card className="px-8 py-2 mt-8">
              <Title level={2}>Basic Information</Title>
              <BasicInformationForm form={form} />
            </Card>
            <Card className="px-8 py-2 mt-8">
              <Title level={2}>Permanent Address</Title>
              <PermanentAddressForm form={form} />
            </Card>
            <Card className="px-8 py-2 mt-8">
              <Title level={2}>Temporary Address</Title>
              <TemporaryAddressForm form={form} />
            </Card>
            <Card className="px-8 py-2 mt-8">
              <Title level={2}>Educational Background</Title>
              <EducationalBackgroundForm
                setLoadingEdu={setLoading}
                form={form}
              />
            </Card>
            <Card className="px-8 py-2 mt-8">
              <Title level={2}>Experience</Title>
              <EexperienceForm form={form} />
            </Card>
            <Card className="px-8 py-2 mt-8">
              <Title level={2}>Certificates</Title>
              <CertificatestForm form={form} />
            </Card>
            <Card className="px-8 py-2 mt-8">
              <CvUpload
                ignoreProfileCheck={true}
                setLoadingCv={setLoading}
                form={form}
                user={{
                  uid: userinfo?.uid ? userinfo.uid : "",
                  cv: userinfo?.cv,
                }}
              />
              <div className="flex flex-col md:flex-row gap-y-3 justify-between pt-12">
                <Button
                  className="h-12 px-8 border-2 border-border text-base flex items-center gap-2"
                  onClick={() => navigate("/enumerator/profile")}
                  disabled={loading}
                >
                  <ClockCircleOutlined />
                  Cancel
                </Button>
                <Button
                  className="h-12 px-8 border-2 border-border text-base flex items-center gap-2 bg-primary text-white border-none"
                  htmlType="submit"
                  type="primary"
                  loading={loading}
                >
                  <CheckOutlined />
                  Finish Setup
                </Button>
              </div>
            </Card>
          </Form>
        </div>
      </div>
    </Spin>
  );
};

export default EnumeratorUpdateProfileForm;
